import { DeviceOperationalState } from '@/__generated__/graphql';
import { Access, useSuspenseAuthorization } from '@features/authorization';
import { useMemo } from 'react';

export const useAllowedTransitions = (deviceId: string) => {
  const { validateAccess } = useSuspenseAuthorization(deviceId);
  return useMemo(() => {
    const edges = (
      [
        [
          validateAccess(Access.UpdateDeviceOperationalState_DevelopmentToInitialization),
          DeviceOperationalState.Development,
          DeviceOperationalState.Initialization,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_DevelopmentToOutOfService),
          DeviceOperationalState.Development,
          DeviceOperationalState.OutOfService,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_InitializationToActive),
          DeviceOperationalState.Initialization,
          DeviceOperationalState.Active,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_InitializationToOutOfService),
          DeviceOperationalState.Initialization,
          DeviceOperationalState.OutOfService,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_ActiveToMaintenance),
          DeviceOperationalState.Active,
          DeviceOperationalState.Maintenance,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_ActiveToOutOfService),
          DeviceOperationalState.Active,
          DeviceOperationalState.OutOfService,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_MaintenanceToActive),
          DeviceOperationalState.Maintenance,
          DeviceOperationalState.Active,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_MaintenanceToOutOfService),
          DeviceOperationalState.Maintenance,
          DeviceOperationalState.OutOfService,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_OutOfServiceToActive),
          DeviceOperationalState.OutOfService,
          DeviceOperationalState.Active,
        ],
      ] as const
    )
      .filter(([valid]) => valid)
      .map((row) => ({ from: row[1], to: row[2] }));

    return {
      getAllowedTransitions(fromState: DeviceOperationalState) {
        return edges.filter(({ from }) => from === fromState).map(({ to }) => to);
      },
      canTransition(from: DeviceOperationalState, to: DeviceOperationalState) {
        return edges.some((t) => t.from === from && t.to === to);
      },
    };
  }, [validateAccess]);
};
